import { useMutation, useQuery } from "@apollo/client";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Menu,
  Modal,
  ScrollArea,
  useMantineTheme,
} from "@mantine/core";
import * as XLSX from "xlsx";
import "jspdf-autotable";
import jsPDF from "jspdf";
import { showNotification } from "@mantine/notifications";
import { GET_ALL_SUPPLIERS } from "apollo/queries";
import Controls from "components/controls/Controls";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import React, { useEffect } from "react";
import { useState } from "react";
import { formatNumber } from "utiles/url";
import DateFilter from "components/controls/DateFilter";

const headerData = [
  {
    label: "Name",
    key: "name",
    sortable: false,
    searchable: true,
    render: (rowData) => <span>{rowData.user.name}</span>,
  },
  {
    label: "Phone",
    key: "Phone",
    sortable: false,
    searchable: true,
    render: (rowData) => <span>{rowData.user.phone}</span>,
  },
  {
    label: "Status",
    key: "account",
    sortable: false,
    searchable: false,
    render: (rowData) => (
      <span>
        {rowData.user.status === "ACTIVE" ? (
          <Badge variant="light" color="green">
            Active
          </Badge>
        ) : (
          <Badge variant="light" color="red">
            Not Active
          </Badge>
        )}
      </span>
    ),
  },
  {
    label: "Total Balance",
    key: "total_balance",
    sortable: false,
    searchable: true,
    render: (rowData) => <span>{formatNumber(rowData.total_balance)}</span>,
  },
  {
    label: "Total Withdrawal",
    key: "total_withdrawal",
    sortable: false,
    searchable: true,
    render: (rowData) => <span>{formatNumber(rowData.total_withdrawal)}</span>,
  },
  {
    label: "Date",
    key: "created_at",
    sortable: true,
    searchable: false,
    render: (rowData) => <span>{rowData.created_at.split(" ")[0]}</span>,
  },
];

const Suppliers = ({ sales }) => {
  const [size, setSize] = useState("10");
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [timeRange, setTimeRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const { data, loading, refetch } = useQuery(GET_ALL_SUPPLIERS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      period: timeRange,
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      sales_id: String(sales.id),
      search: searchValue,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Suppliers",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.getSuppliers.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const theme = useMantineTheme();

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${sales?.user.name} Sales Person Report`, pageWidth / 2, 20, {
      align: "center",
    });
    const headers = headerData.map((col) => col.label);
    const bodyData = data?.getSuppliers?.data?.map((row) => [
      row.user?.name || "N/A",
      row.user?.phone || "N/A",
      row.user?.status === "ACTIVE" ? "Active" : "Not Active",
      formatNumber(row.total_balance) || "N/A",
      formatNumber(row.total_withdrawal) || "N/A",
    ]);

    doc.autoTable({
      startY: 35,
      head: [headers],
      body: bodyData,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [255, 106, 0], textColor: [255, 255, 255] },
    });

    const pageHeight = doc.internal.pageSize.getHeight();
    const exportedDate = `Exported Date: ${new Date().toLocaleString()}`;
    doc.setFontSize(10);
    doc.text(exportedDate, pageWidth - 14, pageHeight - 10, { align: "right" });

    doc.save(`${sales?.user.name}_sales_person_report.pdf`);
  };

  const exportToExcel = () => {
    const bodyData = data?.getSuppliers?.data.map((row) => ({
      Name: row.user?.name || "N/A",
      Phone: row.user?.phone || "N/A",
      Status: row.user?.status === "ACTIVE" ? "Active" : "Not Active",
      "Total Balance": formatNumber(row.total_balance) || "N/A",
      "Total Withdrawal": formatNumber(row.total_withdrawal) || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(bodyData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales_retailers");
    XLSX.writeFile(workbook, `${sales?.user.name}_Sales_retailers.xlsx`);
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          gap: "10px",
          marginTop: "10px",
        }}
      >
        <DateFilter
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          selectedStartDate={selectedStartDate}
          onStartDateChange={setSelectedStartDate}
          selectedEndDate={selectedEndDate}
          onEndDateChange={setSelectedEndDate}
        />
        <Menu shadow="md" trigger="hover" openDelay={100} closeDelay={400}>
          <Menu.Target>
            <Button
              style={{
                width: "80px",
                backgroundColor: "#FF6A00",
                color: "#FFFFFF",
              }}
            >
              Export
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={exportToPDF}>PDF</Menu.Item>
            <Menu.Item onClick={exportToExcel}>Excel</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            paginatorInfo={data.getSuppliers.paginatorInfo}
            searchInput={false}
            loading={loading}
            data={data ? data.getSuppliers.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            flexDirection: "column",
            p: 1,
            gap: 1,
            m: 1,
            bgcolor: "background.paper",
            borderRadius: 1,
          }}
        >
          <div
            style={{
              marginRight: "35px",
              fontWeight: "bold",
              fontSize: "15px",
            }}
          >
            Total Suppliers:{" "}
            {formatNumber(data?.getSuppliers.paginatorInfo.total)}
          </div>
        </Box>
      </Card>
    </div>
  );
};

export default Suppliers;
